import { cx } from "@/lib/cx.js";

/*
 *   <Pagination page={page} totalPages={n} onChange={setPage} />
 *
 * The markup rendered every page number, which is fine for 3 pages and unusable
 * for 40. This collapses the middle with ellipses once there are more than seven,
 * always keeping first, last, current and its neighbours reachable.
 */
export default function Pagination({ page, totalPages, onChange, className }) {
    if (totalPages <= 1) return null;

    const pages = pageWindow(page, totalPages);

    return (
        <nav aria-label="Pagination" className={cx("flex items-center gap-1", className)}>
            <PageButton
                label="‹"
                srLabel="Previous page"
                disabled={page === 1}
                onClick={() => onChange(page - 1)}
            />

            {pages.map((p, i) =>
                p === "…" ? (
                    <span
                        key={`gap-${i}`}
                        className="grid h-7.5 min-w-7.5 place-items-center text-[12.5px] text-slate-400"
                    >
                        …
                    </span>
                ) : (
                    <PageButton
                        key={p}
                        label={p}
                        srLabel={`Page ${p}`}
                        active={p === page}
                        onClick={() => onChange(p)}
                    />
                ),
            )}

            <PageButton
                label="›"
                srLabel="Next page"
                disabled={page === totalPages}
                onClick={() => onChange(page + 1)}
            />
        </nav>
    );
}

function PageButton({ label, srLabel, active, disabled, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-label={srLabel}
            aria-current={active ? "page" : undefined}
            className={cx(
                "grid h-7.5 min-w-7.5 place-items-center rounded-md border px-2 text-[12.5px] font-medium transition",
                active
                    ? "border-brand-600 bg-brand-600 text-white"
                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900 disabled:opacity-40 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300",
            )}
        >
            {label}
        </button>
    );
}

/** [1, "…", 7, 8, 9, "…", 42] — first, last, current and its neighbours. */
function pageWindow(page, total, span = 1) {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

    const out = new Set([1, total]);
    for (let p = page - span; p <= page + span; p++) {
        if (p > 1 && p < total) out.add(p);
    }

    const sorted = [...out].sort((a, b) => a - b);
    const withGaps = [];

    sorted.forEach((p, i) => {
        if (i > 0 && p - sorted[i - 1] > 1) withGaps.push("…");
        withGaps.push(p);
    });

    return withGaps;
}

/**
 * The "Showing 1–20 of 34" line plus a page-size select, as every markup table
 * footer had.
 */
export function PaginationInfo({ page, pageSize, total, onPageSizeChange, sizes = [20, 50, 100], noun = "rows" }) {
    const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const to = Math.min(page * pageSize, total);

    return (
        <>
            <p className="text-[12.5px] text-slate-500 dark:text-slate-400">
                {total === 0 ? `0 ${noun}` : `Showing ${from}–${to} of ${total}`}
            </p>

            {onPageSizeChange && (
                <select
                    aria-label="Rows per page"
                    className="field h-7.5 w-28 py-0 text-[12.5px]"
                    value={pageSize}
                    onChange={(e) => onPageSizeChange(Number(e.target.value))}
                >
                    {sizes.map((s) => (
                        <option key={s} value={s}>
                            {s} / page
                        </option>
                    ))}
                </select>
            )}
        </>
    );
}
